import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useMemo,
  memo,
} from "react";
import { capitalize } from "lodash";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Grid,
  Divider,
  Tooltip,
  Stack,
  Box,
  useMediaQuery,
  Pagination,
  FormControlLabel,
  Switch,
  Checkbox,
  Chip,
  Autocomplete,
  TextField,
  Alert,
} from "@mui/material";
import {
  Close as CloseIcon,
  AutoFixHigh as AutoFixHighIcon,
  ClearAll as ClearAllIcon,
  MyLocation as MyLocationIcon,
  TableRows as TableRowsIcon,
  RemoveCircleOutline as RemoveCircleOutlineIcon,
} from "@mui/icons-material";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ClearIcon from "@mui/icons-material/Clear";
import PreviewPDF from "./PreviewPDF";
import PreviewForm from "./PreviewForm";
import {
  findWidgetsForClickedWidget,
  findWidgetsForPreviewField,
  isPreviewRowField,
} from "./previewFieldSelection";
import {
  isPreviewTextWidget,
  movePreviewFieldAfter,
  swapPreviewFieldOrders,
} from "./previewFieldOrder";
import {
  connectCharacterFields,
  disconnectCharacterFields,
  isConnectedCharacterField,
} from "../../../../../../../utils/fieldConnections";
import { arrayMove } from "@dnd-kit/sortable";
import { applyConnectedTextValues } from "../../../../../../../utils/textFieldConnections";
import DraggablePaper from "../DraggablePaper";

const snapshotFields = (currentProcessingFile) => {
  const fields = currentProcessingFile?.document?.tempFields?.formFields ?? [];
  return fields;
};

const isGenericTableField = (field) =>
  String(field?.field_id || "").toLowerCase() === "generic_table" &&
  Boolean(field?.generic_table?.table_id);

const getPreviewFieldKey = (field) =>
  String(
    isGenericTableField(field)
      ? (field?.field_instance_id ?? field?.id ?? field?.name ?? "")
      : (field?.field_id ?? field?.name ?? ""),
  );

const findWidgetsForRowField = (previewFields = [], field) => {
  if (!isGenericTableField(field)) {
    return findWidgetsForPreviewField(previewFields, field);
  }

  const fieldKey = getPreviewFieldKey(field);
  if (!fieldKey) return [];

  return previewFields.filter(
    (widget) =>
      isGenericTableField(widget) && getPreviewFieldKey(widget) === fieldKey,
  );
};

const canAddFieldToRowSelection = (selectedFields, field) => {
  if (!selectedFields.length) return true;

  const isGenericField = isGenericTableField(field);
  const hasGenericSelection = selectedFields.some(isGenericTableField);

  if (isGenericField !== hasGenericSelection) return false;
  if (!isGenericField) return true;

  const selectedGenericField = selectedFields[0];

  return (
    field?.generic_table?.table_id ===
      selectedGenericField?.generic_table?.table_id &&
    Number(field?.generic_table?.row_index) ===
      Number(selectedGenericField?.generic_table?.row_index)
  );
};

const clearEditorSelection = (currentProcessingFile) => ({
  ...currentProcessingFile,
  document: {
    ...currentProcessingFile?.document,
    tempFields: {
      ...currentProcessingFile?.document?.tempFields,
      selectedFormField: null,
      selectedExtraFields: [],
      selectedPdfTextItem: null,
      selectedExtraPdfTextItems: [],
    },
  },
});

const buildPreviewFile = (
  currentProcessingFile,
  overrideFields,
  selectedPreviewFields = [],
) => ({
  ...clearEditorSelection(currentProcessingFile),
  document: {
    ...currentProcessingFile?.document,
    tempFields: {
      ...currentProcessingFile?.document?.tempFields,
      formFields: overrideFields,
      selectedFormField: selectedPreviewFields[0] ?? null,
      selectedExtraFields: selectedPreviewFields,
      selectedPdfTextItem: null,
      selectedExtraPdfTextItems: [],
    },
  },
});

const PreviewFormDialog = ({
  open,
  setOpen,
  base64File,
  pdfInput,
  currentProcessingFile,
  setCurrentProcessingFile,
  pageNumber,
  displayRotation,
  onDocumentLoadSuccess,
  onPageLoadSuccess,
  titleMetadata,
  fields,
  numPages,
  handlePageChange,
  finalizedEditMode = false,
  onSave,
}) => {
  const isSm = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const [previewFields, setPreviewFields] = useState([]);
  const [enableSorting, setEnableSorting] = useState(true);
  const [fieldDetails, setFieldDetails] = useState(false);
  const [locateFields, setLocateFields] = useState(false);
  const [locatedFields, setLocatedFields] = useState([]);
  const [hoveredFields, setHoveredFields] = useState([]);
  const [rowSelectedFields, setRowSelectedFields] = useState([]);
  const [saving, setSaving] = useState(false);
  const [documentSwapSource, setDocumentSwapSource] = useState(null);
  const [previewFieldScrollRequest, setPreviewFieldScrollRequest] = useState({
    fieldId: "",
    requestId: 0,
  });

  const baseViewPort = useMemo(() => {
    const currentDocument =
      currentProcessingFile?.document?.tempFields?.currentDocument;

    if (currentDocument?.viewport?.transform) {
      return currentDocument.viewport;
    }

    const width = currentDocument?.actualWidth;
    const height = currentDocument?.actualHeight;
    if (!width || !height) return null;

    return {
      width,
      height,
      rotation: 0,
      transform: [1, 0, 0, -1, 0, height],
    };
  }, [currentProcessingFile]);

  useEffect(() => {
    if (open) {
      setCurrentProcessingFile((prev) => clearEditorSelection(prev));
      setPreviewFields(snapshotFields(currentProcessingFile));
      setLocateFields(false);
      setLocatedFields([]);
      setHoveredFields([]);
      setRowSelectedFields([]);
      setDocumentSwapSource(null);
      setPreviewFieldScrollRequest({ fieldId: "", requestId: 0 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const previewProcessingFile = buildPreviewFile(
    currentProcessingFile,
    previewFields,
    locatedFields,
  );

  const setPreviewProcessingFile = useCallback(
    (updater) => {
      setPreviewFields((prevFields) => {
        const next =
          typeof updater === "function"
            ? updater(
                buildPreviewFile(
                  currentProcessingFile,
                  prevFields,
                  locatedFields,
                ),
              )
            : updater;
        return next?.document?.tempFields?.formFields ?? prevFields;
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [currentProcessingFile, locatedFields],
  );

  const handleLocateField = useCallback(
    (field) => {
      const matchingWidgets = findWidgetsForRowField(previewFields, field);

      setLocatedFields(matchingWidgets);

      const targetPage = Number(
        matchingWidgets[0]?.page_num ?? matchingWidgets[0]?.page,
      );
      if (Number.isFinite(targetPage) && targetPage !== pageNumber) {
        handlePageChange(null, targetPage);
      }
    },
    [handlePageChange, pageNumber, previewFields],
  );

  const handleLocateWidget = useCallback(
    (widget, event) => {
      const isSwapClick = Boolean(event?.ctrlKey || event?.metaKey);
      const isSequentialClick = Boolean(event?.altKey);
      const isModifierClick = isSwapClick || isSequentialClick;

      if (
        isModifierClick &&
        isPreviewTextWidget(documentSwapSource) &&
        isPreviewTextWidget(widget) &&
        documentSwapSource?.name !== widget?.name
      ) {
        const updateOrders = (formFields = []) =>
          isSequentialClick
            ? movePreviewFieldAfter(formFields, documentSwapSource, widget)
            : swapPreviewFieldOrders(formFields, documentSwapSource, widget);

        setPreviewFields(updateOrders);
        setCurrentProcessingFile((prevFile) => ({
          ...prevFile,
          document: {
            ...prevFile.document,
            tempFields: {
              ...prevFile.document.tempFields,
              formFields: updateOrders(prevFile.document.tempFields.formFields),
            },
          },
        }));
        setDocumentSwapSource(null);
      } else if (!isModifierClick) {
        setDocumentSwapSource(isPreviewTextWidget(widget) ? widget : null);
      }

      setLocatedFields(findWidgetsForClickedWidget(previewFields, widget));
      setPreviewFieldScrollRequest((request) => ({
        fieldId: widget?.field_id || "",
        requestId: request.requestId + 1,
      }));
    },
    [documentSwapSource, previewFields, setCurrentProcessingFile],
  );

  const handleHoverField = useCallback(
    (field) => {
      setHoveredFields(
        field ? findWidgetsForPreviewField(previewFields, field) : [],
      );
    },
    [previewFields],
  );

  const resetConnectionLocate = useCallback(() => {
    setLocateFields(false);
    setLocatedFields([]);
    setHoveredFields([]);
    setDocumentSwapSource(null);
  }, []);

  const toggleConnectionLocate = useCallback(() => {
    setLocateFields((enabled) => !enabled);
    setLocatedFields([]);
    setHoveredFields([]);
    setDocumentSwapSource(null);
  }, []);

  const handleConnectionTargetHover = useCallback(
    (field) => {
      setHoveredFields(field ? [field] : []);

      if (!field) return;

      const targetPage = Number(field?.page_num ?? field?.page);
      if (Number.isFinite(targetPage) && targetPage !== pageNumber) {
        handlePageChange(null, targetPage);
      }
    },
    [handlePageChange, pageNumber],
  );

  const handleToggleRowField = useCallback((field) => {
    if (!isPreviewRowField(field) && !isGenericTableField(field)) return;

    const fieldKey = getPreviewFieldKey(field);
    if (!fieldKey) return;

    setRowSelectedFields((selectedFields) => {
      const isAlreadySelected = selectedFields.some(
        (selectedField) => getPreviewFieldKey(selectedField) === fieldKey,
      );

      if (isAlreadySelected) {
        return selectedFields.filter(
          (selectedField) => getPreviewFieldKey(selectedField) !== fieldKey,
        );
      }

      return canAddFieldToRowSelection(selectedFields, field)
        ? [...selectedFields, field]
        : selectedFields;
    });
  }, []);

  const handleGroupIntoRow = useCallback(() => {
    if (rowSelectedFields.length < 2) return;

    const selectedWidgets = [];
    const selectedWidgetNames = new Set();

    rowSelectedFields.forEach((field) => {
      findWidgetsForRowField(previewFields, field).forEach((widget) => {
        if (!selectedWidgetNames.has(widget?.name)) {
          selectedWidgetNames.add(widget.name);
          selectedWidgets.push(widget);
        }
      });
    });

    if (selectedWidgets.length < 2) return;

    const rowPrefix = Date.now();
    const rowByWidgetName = new Map(
      selectedWidgets.map((widget, index) => [
        widget.name,
        `${rowPrefix}_${index + 1}`,
      ]),
    );
    const groupFields = (formFields = []) =>
      formFields.map((field) =>
        rowByWidgetName.has(field?.name)
          ? {
              ...field,
              row: rowByWidgetName.get(field.name),
              ...(isGenericTableField(field) && field?.generic_table
                ? {
                    generic_table: {
                      ...field.generic_table,
                      row_group_disabled: false,
                    },
                  }
                : {}),
            }
          : field,
      );

    setPreviewFields(groupFields);
    setCurrentProcessingFile((prevFile) => ({
      ...prevFile,
      document: {
        ...prevFile.document,
        tempFields: {
          ...prevFile.document.tempFields,
          formFields: groupFields(prevFile.document.tempFields.formFields),
        },
      },
    }));
    setRowSelectedFields([]);
  }, [previewFields, rowSelectedFields, setCurrentProcessingFile]);

  const selectedRowsByPrefix = useMemo(() => {
    const rowsByPrefix = new Map();

    rowSelectedFields.forEach((field) => {
      findWidgetsForRowField(previewFields, field).forEach((widget) => {
        const row = widget?.row;
        const prefix = row?.split("_")?.[0];
        if (!prefix || !row) return;

        if (!rowsByPrefix.has(prefix)) {
          rowsByPrefix.set(prefix, new Set());
        }
        rowsByPrefix.get(prefix).add(row);
      });
    });

    return rowsByPrefix;
  }, [previewFields, rowSelectedFields]);

  const selectedRowPrefixes = useMemo(
    () => new Set(selectedRowsByPrefix.keys()),
    [selectedRowsByPrefix],
  );

  const handleRemoveRowGroup = useCallback(() => {
    if (selectedRowPrefixes.size === 0) return;

    const ungroupFields = (formFields = []) => {
      const groupSizes = formFields.reduce((sizes, field) => {
        const rowPrefix = field?.row?.split("_")?.[0];
        if (rowPrefix && selectedRowPrefixes.has(rowPrefix)) {
          sizes.set(rowPrefix, (sizes.get(rowPrefix) || 0) + 1);
        }
        return sizes;
      }, new Map());

      return formFields.map((field) => {
        const row = field?.row;
        const rowPrefix = row?.split("_")?.[0];
        if (!rowPrefix || !selectedRowPrefixes.has(rowPrefix)) return field;

        const removeEntireGroup = (groupSizes.get(rowPrefix) || 0) <= 2;
        const isSelectedField = selectedRowsByPrefix.get(rowPrefix)?.has(row);
        if (!removeEntireGroup && !isSelectedField) return field;

        return {
          ...field,
          row: "",
          ...(isGenericTableField(field) && field?.generic_table
            ? {
                generic_table: {
                  ...field.generic_table,
                  row_group_disabled: true,
                },
              }
            : {}),
        };
      });
    };

    setPreviewFields(ungroupFields);
    setCurrentProcessingFile((prevFile) => ({
      ...prevFile,
      document: {
        ...prevFile.document,
        tempFields: {
          ...prevFile.document.tempFields,
          formFields: ungroupFields(prevFile.document.tempFields.formFields),
        },
      },
    }));
    setRowSelectedFields([]);
  }, [selectedRowPrefixes, selectedRowsByPrefix, setCurrentProcessingFile]);

  const handleSave = useCallback(async () => {
    if (!onSave || saving) return;

    try {
      setSaving(true);
      const saved = await onSave();
      if (saved !== false) setOpen(false, { saved: true });
    } finally {
      setSaving(false);
    }
  }, [onSave, saving, setOpen]);

  const fillDemoRef = useRef(null);
  const clearRef = useRef(null);

  const getConnectionFieldKey = (field) =>
    String(field?.field_instance_id ?? field?.name ?? "");

  const getConnectionFieldLabel = (field) =>
    field?.field_label ||
    field?.label ||
    field?.field_name ||
    field?.name ||
    "Text field";

  const isConnectionTarget = (field) =>
    Boolean(
      field?.type === "PDFTextField" &&
        field?.field_id &&
        getConnectionFieldKey(field) &&
        !String(field?.name || "").startsWith("block__") &&
        !field?.generic_table &&
        field?.location !== "sign",
    );

  const createConnectionGroupId = () =>
    typeof crypto !== "undefined" && crypto?.randomUUID
      ? crypto.randomUUID()
      : `connection-${Date.now()}-${Math.random().toString(36).slice(2)}`;

  const collectPreviewConnectionGroups = (fields = []) => {
    const groups = new Map();

    fields.forEach((field) => {
      const character = isConnectedCharacterField(field);
      const connection = character ? field.connection : field?.text_connection;
      if (!connection?.group_id) return;

      const mode = character ? "character" : "multiline";
      const key = `${mode}:${connection.group_id}`;
      if (!groups.has(key)) {
        groups.set(key, {
          key,
          mode,
          groupId: connection.group_id,
          sourceFieldId: connection.source_field_id,
          sourceLabel:
            connection.source_field_label || getConnectionFieldLabel(field),
          staticPrefix: connection.static_prefix || "",
          sourceValue: connection.source_value,
          targets: [],
        });
      }
      groups.get(key).targets.push(field);
    });

    return [...groups.values()]
      .map((group) => ({
        ...group,
        targets: group.targets.sort((a, b) =>
          group.mode === "character"
            ? a.connection.character_index - b.connection.character_index
            : a.text_connection.widget_index - b.text_connection.widget_index,
        ),
      }))
      .sort((a, b) => a.sourceLabel.localeCompare(b.sourceLabel));
  };

  const removeConnectionGroup = (fields, group) => {
    if (!group) return fields;

    return fields.map((field) => {
      if (
        group.mode === "character" &&
        field?.connection?.group_id === group.groupId
      ) {
        const disconnectedField = { ...field };
        delete disconnectedField.connection;
        return disconnectedField;
      }

      if (
        group.mode === "multiline" &&
        field?.text_connection?.group_id === group.groupId
      ) {
        const disconnectedField = { ...field };
        delete disconnectedField.text_connection;
        return disconnectedField;
      }

      return field;
    });
  };

  const PreviewConnectionManager = useMemo(
    () =>
      memo(
        ({
          fields = [],
          catalog = [],
          onCommit,
          locateFields,
          onToggleLocate,
          onHoverTarget,
          onResetLocate,
        }) => {
          const [open, setOpen] = useState(false);
          const [view, setView] = useState("character");
          const [mode, setMode] = useState("character");
          const [editingGroupKey, setEditingGroupKey] = useState("");
          const [targetKeys, setTargetKeys] = useState([]);
          const [checkedTargetKeys, setCheckedTargetKeys] = useState([]);
          const [sourceFieldId, setSourceFieldId] = useState("");
          const [staticPrefix, setStaticPrefix] = useState("");
          const [error, setError] = useState("");

          const groups = useMemo(
            () => collectPreviewConnectionGroups(fields),
            [fields],
          );
          const editingGroup =
            groups.find((group) => group.key === editingGroupKey) || null;
          const editingTargetKeys = useMemo(
            () =>
              new Set(editingGroup?.targets.map(getConnectionFieldKey) || []),
            [editingGroup],
          );
          const selectedTargetFieldId = targetKeys.length
            ? fields.find(
                (field) => getConnectionFieldKey(field) === targetKeys[0],
              )?.field_id
            : "";
          const targetOptions = useMemo(
            () =>
              fields.filter(
                (field) =>
                  isConnectionTarget(field) &&
                  (!selectedTargetFieldId ||
                    field.field_id === selectedTargetFieldId ||
                    editingTargetKeys.has(getConnectionFieldKey(field))) &&
                  ((!field.connection && !field.text_connection) ||
                    editingTargetKeys.has(getConnectionFieldKey(field))),
              ),
            [editingTargetKeys, fields, selectedTargetFieldId],
          );
          const targets = targetKeys
            .map((key) =>
              fields.find((field) => getConnectionFieldKey(field) === key),
            )
            .filter(Boolean);
          const selectedTargetKeys = new Set(targetKeys);
          const allTargetOptionsSelected =
            targetOptions.length > 0 &&
            targetOptions.every((field) =>
              selectedTargetKeys.has(getConnectionFieldKey(field)),
            );
          const sourceOptions = useMemo(() => {
            const targetIds = new Set(targets.map((field) => field.field_id));
            const byId = new Map();

            [...catalog, ...fields].forEach((field) => {
              if (field?.field_id && !byId.has(field.field_id)) {
                byId.set(field.field_id, field);
              }
            });
            groups
              .filter((group) => group.mode === "character")
              .forEach((group) => {
                if (!byId.has(group.sourceFieldId)) {
                  byId.set(group.sourceFieldId, {
                    field_id: group.sourceFieldId,
                    field_label: group.sourceLabel,
                    field_type: "text",
                  });
                }
              });

            return [...byId.values()]
              .filter((field) => {
                const type = String(
                  field?.field_type ||
                    (field?.type === "PDFTextField" ? "text" : field?.type) ||
                    "",
                ).toLowerCase();

                return (
                  !targetIds.has(field?.field_id) &&
                  !field?.connection &&
                  !field?.text_connection &&
                  !field?.generic_table &&
                  field?.location !== "sign" &&
                  ["text", "number", "multiline"].includes(type)
                );
              })
              .sort((a, b) =>
                getConnectionFieldLabel(a).localeCompare(
                  getConnectionFieldLabel(b),
                ),
              );
          }, [catalog, fields, groups, targets]);
          const source =
            sourceOptions.find((field) => field.field_id === sourceFieldId) ||
            null;

          const startNew = () => {
            onResetLocate?.();
            setEditingGroupKey("");
            setView("character");
            setMode("character");
            setTargetKeys([]);
            setCheckedTargetKeys([]);
            setSourceFieldId("");
            setStaticPrefix("");
            setError("");
            setOpen(true);
          };

          const openManager = () => {
            onResetLocate?.();
            setEditingGroupKey("");
            setView(groups.length ? "connected" : "character");
            setMode("character");
            setTargetKeys([]);
            setCheckedTargetKeys([]);
            setSourceFieldId("");
            setStaticPrefix("");
            setError("");
            setOpen(true);
          };

          const editGroup = (group) => {
            onResetLocate?.();
            setEditingGroupKey(group.key);
            setView(group.mode);
            setMode(group.mode);
            setTargetKeys(group.targets.map(getConnectionFieldKey));
            setCheckedTargetKeys([]);
            setSourceFieldId(
              group.mode === "character" ? group.sourceFieldId : "",
            );
            setStaticPrefix(group.staticPrefix || "");
            setError("");
            setOpen(true);
          };

          const close = () => {
            onResetLocate?.();
            setOpen(false);
            setCheckedTargetKeys([]);
            setError("");
          };

          const moveTarget = (index, offset) => {
            const nextIndex = index + offset;
            if (nextIndex < 0 || nextIndex >= targetKeys.length) return;
            setTargetKeys((current) => arrayMove(current, index, nextIndex));
          };

          const save = () => {
            try {
              if (!targets.length)
                throw new Error("Select at least one PDF text widget.");

              const baseFields = removeConnectionGroup(fields, editingGroup);
              const liveTargets = targetKeys
                .map((key) =>
                  baseFields.find(
                    (field) => getConnectionFieldKey(field) === key,
                  ),
                )
                .filter(Boolean);

              if (liveTargets.length !== targetKeys.length) {
                throw new Error(
                  "One or more selected widgets are no longer available.",
                );
              }
              if (
                new Set(liveTargets.map((field) => field.field_id)).size !== 1
              ) {
                throw new Error(
                  "Connected widgets must use the same field ID.",
                );
              }

              let updatedFields;
              if (mode === "character") {
                if (!source) throw new Error("Select a source field.");
                const preserveGroup =
                  editingGroup?.mode === "character" &&
                  editingGroup.sourceFieldId === source.field_id;
                updatedFields = connectCharacterFields(
                  baseFields,
                  liveTargets,
                  source,
                  {
                    groupId: preserveGroup
                      ? editingGroup.groupId
                      : createConnectionGroupId(),
                    stripSeparators: Boolean(
                      editingGroup?.targets?.[0]?.connection?.strip_separators,
                    ),
                    staticPrefix,
                  },
                );
              } else {
                if (liveTargets.length < 2) {
                  throw new Error("Multiline requires at least two widgets.");
                }
                if (
                  liveTargets.some(
                    (field) => field.connection || field.text_connection,
                  )
                ) {
                  throw new Error("Disconnect the existing connection first.");
                }

                const groupId =
                  editingGroup?.mode === "multiline"
                    ? editingGroup.groupId
                    : createConnectionGroupId();
                const keyIndexes = new Map(
                  liveTargets.map((field, index) => [
                    getConnectionFieldKey(field),
                    index,
                  ]),
                );
                updatedFields = baseFields.map((field) => {
                  if (!keyIndexes.has(getConnectionFieldKey(field)))
                    return field;

                  const width = Number(
                    field?.rect?.W ?? field?.rect?.w ?? field?.width,
                  );
                  const height = Number(
                    field?.rect?.H ?? field?.rect?.h ?? field?.height,
                  );

                  return {
                    ...field,
                    text_connection: {
                      group_id: groupId,
                      source_field_id: liveTargets[0].field_id,
                      source_field_label: getConnectionFieldLabel(
                        liveTargets[0],
                      ),
                      widget_index: keyIndexes.get(
                        getConnectionFieldKey(field),
                      ),
                      widget_count: liveTargets.length,
                      width,
                      height,
                      ...(editingGroup?.sourceValue !== undefined
                        ? { source_value: editingGroup.sourceValue }
                        : {}),
                    },
                  };
                });
                updatedFields = applyConnectedTextValues(updatedFields).fields;
              }

              onCommit(updatedFields);
              close();
            } catch (saveError) {
              setError(saveError.message);
            }
          };

          const disconnect = () => {
            if (!editingGroup) return;
            const updatedFields =
              editingGroup.mode === "character"
                ? disconnectCharacterFields(fields, editingGroup.targets)
                : removeConnectionGroup(fields, editingGroup);
            onCommit(updatedFields);
            close();
          };

          const disconnectGroup = (group) => {
            const updatedFields =
              group.mode === "character"
                ? disconnectCharacterFields(fields, group.targets)
                : removeConnectionGroup(fields, group);
            onCommit(updatedFields);
            setEditingGroupKey("");
            setError("");
          };

          return (
            <>
              <Tooltip title="Connect PDF text widgets to fill together">
                <Button
                  size="small"
                  variant={groups.length > 0 ? "contained" : "outlined"}
                  color="secondary"
                  startIcon={<LinkIcon />}
                  onClick={openManager}
                  sx={{
                    textTransform: "none",
                    borderRadius: 2,
                    fontWeight: 600,
                  }}
                >
                  {groups.length > 0
                    ? `Connected (${groups.length})`
                    : "Connect Fields"}
                </Button>
              </Tooltip>

              <Dialog
                open={open}
                onClose={close}
                PaperComponent={DraggablePaper}
                PaperProps={{
                  dragHandle: ".connect-fields-drag-handle",
                  dragCancel:
                    'button, [role="button"], input, textarea, select, a, [class*="MuiDialogContent-root"]',
                }}
                aria-labelledby="draggable-dialog-title"
                fullWidth
                maxWidth="sm"
                slotProps={{
                  backdrop: {
                    sx: {
                      backgroundColor: "rgba(0, 0, 0, 0.2)",
                    },
                  },
                }}
              >
                <DialogTitle
                  id="draggable-dialog-title"
                  className="connect-fields-drag-handle"
                  style={{ cursor: "move" }}
                >
                  {editingGroup ? "Modify Connection" : "Connect Fields"}
                </DialogTitle>
                <DialogContent dividers>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="subtitle2" mb={0.75}>
                        Connection type
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          variant={
                            view === "character" ? "contained" : "outlined"
                          }
                          onClick={() => {
                            onResetLocate?.();
                            setView("character");
                            setMode("character");
                            setEditingGroupKey("");
                            setTargetKeys([]);
                            setCheckedTargetKeys([]);
                            setSourceFieldId("");
                            setStaticPrefix("");
                            setError("");
                          }}
                          sx={{ textTransform: "none" }}
                        >
                          Character
                        </Button>
                        <Button
                          size="small"
                          variant={
                            view === "multiline" ? "contained" : "outlined"
                          }
                          onClick={() => {
                            onResetLocate?.();
                            setView("multiline");
                            setMode("multiline");
                            setEditingGroupKey("");
                            setTargetKeys([]);
                            setCheckedTargetKeys([]);
                            setSourceFieldId("");
                            setStaticPrefix("");
                            setError("");
                          }}
                          sx={{ textTransform: "none" }}
                        >
                          Multiline
                        </Button>
                        {groups.length > 0 && (
                          <Button
                            size="small"
                            variant={
                              view === "connected" ? "contained" : "outlined"
                            }
                            startIcon={<LinkIcon />}
                            onClick={() => {
                              onResetLocate?.();
                              setView("connected");
                              setEditingGroupKey("");
                              setTargetKeys([]);
                              setCheckedTargetKeys([]);
                              setSourceFieldId("");
                              setStaticPrefix("");
                              setError("");
                            }}
                            sx={{ textTransform: "none" }}
                          >
                            Connected Fields ({groups.length})
                          </Button>
                        )}
                      </Stack>
                    </Box>

                    {view === "connected" ? (
                      groups.length ? (
                        <Stack spacing={1}>
                          {groups.map((group) => (
                            <Box
                              key={group.key}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                p: 1.25,
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 1.5,
                              }}
                            >
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Stack
                                  direction="row"
                                  spacing={0.75}
                                  alignItems="center"
                                >
                                  <Chip
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    label={
                                      group.mode === "character"
                                        ? "Character"
                                        : "Multiline"
                                    }
                                  />
                                  <Typography
                                    variant="body2"
                                    fontWeight={700}
                                    noWrap
                                  >
                                    {group.sourceLabel}
                                  </Typography>
                                </Stack>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {group.targets.length} connected widget
                                  {group.targets.length === 1 ? "" : "s"}
                                </Typography>
                              </Box>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => editGroup(group)}
                                sx={{ textTransform: "none" }}
                              >
                                Edit
                              </Button>
                              <Button
                                size="small"
                                color="error"
                                startIcon={<LinkOffIcon />}
                                onClick={() => disconnectGroup(group)}
                                sx={{ textTransform: "none" }}
                              >
                                Disconnect
                              </Button>
                            </Box>
                          ))}
                        </Stack>
                      ) : (
                        <Box sx={{ py: 4, textAlign: "center" }}>
                          <Typography variant="body1" fontWeight={600}>
                            No connected fields
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            mb={1.5}
                          >
                            Create a character or multiline connection first.
                          </Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<LinkIcon />}
                            onClick={startNew}
                            sx={{ textTransform: "none" }}
                          >
                            Connect Fields
                          </Button>
                        </Box>
                      )
                    ) : (
                      <>
                        <Autocomplete
                          multiple
                          options={targetOptions}
                          value={targets}
                          filterSelectedOptions
                          limitTags={3}
                          getOptionKey={getConnectionFieldKey}
                          getOptionLabel={(option) =>
                            `${getConnectionFieldLabel(option)} — ${option.name || option.field_id}`
                          }
                          isOptionEqualToValue={(option, selected) =>
                            getConnectionFieldKey(option) ===
                            getConnectionFieldKey(selected)
                          }
                          onChange={(_, selectedTargets) => {
                            const nextTargetKeys = selectedTargets.map(
                              getConnectionFieldKey,
                            );
                            setTargetKeys(nextTargetKeys);
                            setCheckedTargetKeys((current) =>
                              current.filter((key) =>
                                nextTargetKeys.includes(key),
                              ),
                            );
                            if (nextTargetKeys.length) {
                              onHoverTarget?.(null);
                            } else {
                              onResetLocate?.();
                            }
                            setError("");
                          }}
                          renderTags={(selectedTargets, getTagProps) =>
                            selectedTargets.map((target, index) => {
                              const { key, ...tagProps } = getTagProps({
                                index,
                              });

                              return (
                                <Chip
                                  key={key}
                                  {...tagProps}
                                  size="small"
                                  label={`${getConnectionFieldLabel(target)} — ${target.name || target.field_id}`}
                                  sx={{
                                    maxWidth: 190,
                                    "& .MuiChip-label": {
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    },
                                  }}
                                />
                              );
                            })
                          }
                          sx={{
                            "& .MuiAutocomplete-inputRoot": {
                              alignItems: "flex-start",
                              flexWrap: "wrap",
                              rowGap: 0.5,
                            },
                            "& .MuiAutocomplete-input": {
                              minWidth: targets.length
                                ? "24px !important"
                                : "180px !important",
                            },
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="PDF text widgets"
                              placeholder={
                                targets.length ? "" : "Select fields to connect"
                              }
                              helperText="Only widgets with the same field ID can be connected."
                            />
                          )}
                        />

                        {targets.length > 0 && (
                          <Stack
                            direction="row"
                            spacing={1}
                            justifyContent="flex-end"
                          >
                            <Button
                              size="small"
                              variant={locateFields ? "contained" : "outlined"}
                              color="secondary"
                              startIcon={<MyLocationIcon />}
                              onClick={onToggleLocate}
                              aria-pressed={locateFields}
                              sx={{ textTransform: "none" }}
                            >
                              {locateFields
                                ? "Locating Fields"
                                : "Locate Fields"}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={allTargetOptionsSelected}
                              onClick={() => {
                                setTargetKeys(
                                  targetOptions.map(getConnectionFieldKey),
                                );
                                setError("");
                              }}
                              sx={{ textTransform: "none" }}
                            >
                              Select all
                            </Button>
                          </Stack>
                        )}

                        {targets.length > 0 && (
                          <Stack spacing={0.5}>
                            <Typography variant="subtitle2">
                              Widget order
                            </Typography>
                            {targets.map((target, index) => (
                              <Box
                                key={getConnectionFieldKey(target)}
                                onMouseEnter={() => {
                                  if (locateFields) onHoverTarget?.(target);
                                }}
                                onMouseLeave={() => {
                                  if (locateFields) onHoverTarget?.(null);
                                }}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                  px: 1,
                                  py: 0.5,
                                  border: "1px solid",
                                  borderColor: "divider",
                                  borderRadius: 1,
                                }}
                              >
                                <Tooltip title="Mark widget as reviewed (not saved)">
                                  <Checkbox
                                    size="small"
                                    checked={checkedTargetKeys.includes(
                                      getConnectionFieldKey(target),
                                    )}
                                    onChange={(event) => {
                                      const targetKey =
                                        getConnectionFieldKey(target);
                                      setCheckedTargetKeys((current) =>
                                        event.target.checked
                                          ? [
                                              ...new Set([
                                                ...current,
                                                targetKey,
                                              ]),
                                            ]
                                          : current.filter(
                                              (key) => key !== targetKey,
                                            ),
                                      );
                                    }}
                                    inputProps={{
                                      "aria-label": `Mark widget ${index + 1} as reviewed`,
                                    }}
                                    sx={{ p: 0.25 }}
                                  />
                                </Tooltip>
                                <Typography
                                  variant="body2"
                                  sx={{ flex: 1 }}
                                  noWrap
                                >
                                  {index + 1}. {getConnectionFieldLabel(target)}{" "}
                                  — {target.name}
                                </Typography>
                                <IconButton
                                  size="small"
                                  disabled={index === 0}
                                  aria-label="Move widget up"
                                  onClick={() => moveTarget(index, -1)}
                                >
                                  <ArrowUpwardIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  disabled={index === targets.length - 1}
                                  aria-label="Move widget down"
                                  onClick={() => moveTarget(index, 1)}
                                >
                                  <ArrowDownwardIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  aria-label="Remove widget"
                                  onClick={() => {
                                    const nextTargetKeys = targetKeys.filter(
                                      (key) =>
                                        key !== getConnectionFieldKey(target),
                                    );
                                    setTargetKeys(nextTargetKeys);
                                    setCheckedTargetKeys((current) =>
                                      current.filter(
                                        (key) =>
                                          key !== getConnectionFieldKey(target),
                                      ),
                                    );
                                    if (nextTargetKeys.length) {
                                      onHoverTarget?.(null);
                                    } else {
                                      onResetLocate?.();
                                    }
                                  }}
                                >
                                  <ClearIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            ))}
                          </Stack>
                        )}

                        {mode === "character" ? (
                          <>
                            <Autocomplete
                              options={sourceOptions}
                              value={source}
                              getOptionKey={(option) => option.field_id}
                              getOptionLabel={getConnectionFieldLabel}
                              isOptionEqualToValue={(option, selected) =>
                                option.field_id === selected.field_id
                              }
                              onChange={(_, selectedSource) => {
                                setSourceFieldId(
                                  selectedSource?.field_id || "",
                                );
                                setError("");
                              }}
                              noOptionsText="No eligible source fields"
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Connect to"
                                  placeholder="Select source field"
                                />
                              )}
                            />
                            <TextField
                              label="Static prefix already printed on PDF"
                              placeholder="Optional"
                              value={staticPrefix}
                              onChange={(event) => {
                                setStaticPrefix(event.target.value);
                                setError("");
                              }}
                            />
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Text entered once will continue through the selected
                            widgets in order.
                          </Typography>
                        )}
                      </>
                    )}

                    {error && <Alert severity="error">{error}</Alert>}
                  </Stack>
                </DialogContent>
                <DialogActions
                  className="connect-fields-drag-handle"
                  sx={{ cursor: "move", "& button": { cursor: "pointer" } }}
                >
                  {view === "connected" ? (
                    <Button onClick={close} sx={{ textTransform: "none" }}>
                      Close
                    </Button>
                  ) : (
                    <>
                      {editingGroup && (
                        <Button
                          color="error"
                          startIcon={<LinkOffIcon />}
                          onClick={disconnect}
                          sx={{ mr: "auto", textTransform: "none" }}
                        >
                          Disconnect
                        </Button>
                      )}
                      <Button onClick={close} sx={{ textTransform: "none" }}>
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        onClick={save}
                        sx={{ textTransform: "none" }}
                      >
                        {editingGroup ? "Save Connection" : "Connect"}
                      </Button>
                    </>
                  )}
                </DialogActions>
              </Dialog>
            </>
          );
        },
      ),
    [],
  );

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: { height: "90vh", display: "flex", flexDirection: "column" },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
        }}
      >
        <Typography fontWeight={600}>Form Preview</Typography>

        {/* Preview controls */}
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControlLabel
            control={
              <Switch
                checked={enableSorting}
                onChange={(event) => setEnableSorting(event.target.checked)}
              />
            }
            label={enableSorting ? "Disable Sorting" : "Enable Sorting"}
          />
          <FormControlLabel
            control={
              <Switch
                checked={fieldDetails}
                onChange={(event) => setFieldDetails(event.target.checked)}
              />
            }
            label={fieldDetails ? "Hide Details" : "Show Details"}
          />
          <Tooltip title="Click either a form field or a document widget to locate every linked instance">
            <Button
              size="small"
              variant={locateFields ? "contained" : "outlined"}
              color="secondary"
              startIcon={<MyLocationIcon />}
              onClick={() => {
                setLocateFields((enabled) => !enabled);
                setLocatedFields([]);
                setHoveredFields([]);
                setDocumentSwapSource(null);
              }}
              aria-pressed={locateFields}
              sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}
            >
              {locateFields ? "Locating Fields" : "Locate Fields"}
            </Button>
          </Tooltip>
          <PreviewConnectionManager
            fields={previewFields}
            catalog={fields}
            locateFields={locateFields}
            onToggleLocate={toggleConnectionLocate}
            onHoverTarget={handleConnectionTargetHover}
            onResetLocate={resetConnectionLocate}
            onCommit={(updatedFields) => {
              setPreviewFields(updatedFields);
              setCurrentProcessingFile((prevFile) => ({
                ...prevFile,
                document: {
                  ...prevFile.document,
                  tempFields: {
                    ...prevFile.document.tempFields,
                    formFields: updatedFields,
                  },
                },
              }));
            }}
          />
          <Tooltip
            title={
              rowSelectedFields.length < 2
                ? "Ctrl/Cmd-click at least two form fields to select them"
                : `Group ${rowSelectedFields.length} selected fields into one row`
            }
          >
            <span>
              <Button
                size="small"
                variant="outlined"
                color="primary"
                startIcon={<TableRowsIcon />}
                disabled={rowSelectedFields.length < 2}
                onClick={handleGroupIntoRow}
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  fontWeight: 600,
                }}
              >
                Group into Row
                {rowSelectedFields.length > 0
                  ? ` (${rowSelectedFields.length})`
                  : ""}
              </Button>
            </span>
          </Tooltip>
          <Tooltip
            title={
              selectedRowPrefixes.size === 0
                ? "Ctrl/Cmd-click a field in an existing row group"
                : `Remove ${selectedRowPrefixes.size} selected row ${
                    selectedRowPrefixes.size === 1 ? "group" : "groups"
                  }`
            }
          >
            <span>
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<RemoveCircleOutlineIcon />}
                disabled={selectedRowPrefixes.size === 0}
                onClick={handleRemoveRowGroup}
                sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}
              >
                Remove Row Group
              </Button>
            </span>
          </Tooltip>

          <Tooltip title="Populate all fields with representative sample data">
            <Button
              size="small"
              variant="outlined"
              color="primary"
              startIcon={<AutoFixHighIcon />}
              onClick={() => fillDemoRef.current?.()}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                fontWeight: 600,
              }}
            >
              Fill with Demo Data
            </Button>
          </Tooltip>

          <Tooltip title="Reset all fields to empty">
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<ClearAllIcon />}
              onClick={() => clearRef.current?.()}
              sx={{ textTransform: "none", borderRadius: 2 }}
            >
              Clear
            </Button>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          <IconButton onClick={() => setOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent
        sx={{
          flex: 1,
          overflow: "auto",
          backgroundColor: "#f5f5f5",
          px: 0,
          py: 0,
        }}
      >
        <Grid container sx={{ overflow: "hidden", height: "100%" }}>
          <Grid item xs={12} sm={6} sx={{ height: "100%", minWidth: 0 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mb: 1,
              }}
            >
              {numPages > 1 && (
                <Pagination
                  count={numPages}
                  page={pageNumber}
                  onChange={handlePageChange}
                  size="small"
                />
              )}
            </Box>
            <Box
              sx={{
                overflow: "auto",
                width: "100%",
                height: "calc(100% - 36px)",
              }}
            >
              <PreviewPDF
                base64File={base64File}
                pdfInput={pdfInput}
                currentProcessingFile={previewProcessingFile}
                setCurrentProcessingFile={setPreviewProcessingFile}
                pageNumber={pageNumber}
                displayRotation={displayRotation}
                onDocumentLoadSuccess={onDocumentLoadSuccess}
                onPageLoadSuccess={onPageLoadSuccess}
                baseViewPort={baseViewPort}
                formFields={fields}
                locateFields={locateFields}
                onLocateWidget={handleLocateWidget}
                hoveredFields={hoveredFields}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} sx={{ height: "100%", minWidth: 0 }}>
            <Box
              sx={{
                backgroundColor: "#fff",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                p: "12px 0px !important",
                mb: "4px",
              }}
            >
              <Typography
                color="primary"
                mx={1}
                align="center"
                variant={isSm ? "h6" : "h5"}
              >
                {titleMetadata?.title || "N/A"}
              </Typography>
              <Typography
                color="primary"
                variant="h6"
                sx={{ fontSize: "14px !important" }}
              >
                {titleMetadata?.pageNumber || 0} /{" "}
                {titleMetadata?.numPages || 0}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  mb: 0.5,
                  mt: 0,
                  fontSize: "14px !important",
                }}
                textAlign="center"
              >
                {titleMetadata?.municipality
                  ? `${capitalize(titleMetadata?.municipality)} - ${capitalize(
                      titleMetadata?.county,
                    )} County`
                  : `${capitalize(titleMetadata?.county)} County`}
              </Typography>
            </Box>
            <PreviewForm
              currentProcessingFile={previewProcessingFile}
              setCurrentProcessingFile={setPreviewProcessingFile}
              fillDemoRef={fillDemoRef}
              clearRef={clearRef}
              fields={fields}
              setDocCurrentProcessingFile={setCurrentProcessingFile}
              enableSorting={enableSorting}
              fieldDetails={fieldDetails}
              locateFields={locateFields}
              locatedField={locatedFields[0]}
              onLocateField={handleLocateField}
              onHoverField={handleHoverField}
              rowSelectedFields={rowSelectedFields}
              onToggleRowField={handleToggleRowField}
              scrollToFieldRequest={previewFieldScrollRequest}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={() => setOpen(false)}
          variant={finalizedEditMode ? "outlined" : "contained"}
          disabled={saving}
          sx={{ textTransform: "none", borderRadius: 2 }}
        >
          Close Preview
        </Button>
        {finalizedEditMode && (
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            {saving ? "Updating..." : "Update Document"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PreviewFormDialog;
